/**
 * 화면 아래에 떠 있는 권유 카드들의 자리 조정 (baikal/rnd/bareunai#291).
 *
 * 알림 구독 권유(#264 `PushOptInPrompt`)와 별점 창(#291 `SiteReviewPrompt`)은 둘 다
 * 화면 아래에 떠 있는 카드다. 모바일에서는 둘 다 아래 전체 폭이라 함께 뜨면 겹치고,
 * 데스크톱에서도 한 화면에 권유가 둘이면 잔소리가 된다. 그래서 **한 번에 하나만**
 * 보이게 한다. 판정은 이 모듈 한 곳에서만 한다. 카드마다 서로를 알고 숨기 시작하면
 * 카드가 늘 때마다 조합이 늘어난다.
 *
 * 규칙: 보이고 싶은 카드가 여럿이면 `FLOATING_PROMPT_PRIORITY` 에서 앞선 것 하나만
 * 보인다. 앞선 카드가 사라지면 가려져 있던 카드가 다시 보인다(권유가 없어지는 것이
 * 아니라 잠시 비켜 있는 것이다).
 */
import { useEffect, useSyncExternalStore } from 'react';

export type FloatingPromptId = 'site-review' | 'push-opt-in';

/**
 * 앞에 있을수록 우선한다.
 *
 * 별점 창이 앞선다. 방금 한 행동(전체 복사)에 대한 질문이라 그 순간이 지나면 받을 수
 * 없지만, 알림 권유는 별점 창이 닫힌 뒤에 다시 보여도 된다.
 */
export const FLOATING_PROMPT_PRIORITY: readonly FloatingPromptId[] = [
  'site-review',
  'push-opt-in'
];

/** 보이고 싶어 하는 카드 가운데 지금 보일 하나. 없으면 null. */
export function pickVisiblePrompt(
  requests: ReadonlySet<FloatingPromptId>
): FloatingPromptId | null {
  return FLOATING_PROMPT_PRIORITY.find((id) => requests.has(id)) ?? null;
}

export interface FloatingPromptStore {
  /** 이 카드가 보이고 싶다고 알린다. 같은 카드를 거듭 알려도 한 번으로 친다. */
  request(id: FloatingPromptId): void;
  /** 이 카드가 더는 보이지 않아도 된다고 알린다(닫힘·언마운트). */
  release(id: FloatingPromptId): void;
  /** 지금 보일 카드. */
  visible(): FloatingPromptId | null;
  /** 보일 카드가 바뀔 때마다 부른다. 해제 함수를 돌려준다. */
  subscribe(listener: () => void): () => void;
}

/**
 * 자리 판정 저장소. 페이지 안의 모든 카드가 한 저장소(`floatingPromptStore`)를 쓴다.
 * 테스트에서는 새로 만들어 서로 섞이지 않게 한다.
 */
export function createFloatingPromptStore(): FloatingPromptStore {
  const requests = new Set<FloatingPromptId>();
  const listeners = new Set<() => void>();
  let current: FloatingPromptId | null = null;

  // 보일 카드가 실제로 바뀔 때만 알린다. 같은 결과로 다시 그리지 않게.
  const update = () => {
    const next = pickVisiblePrompt(requests);
    if (next === current) return;
    current = next;
    listeners.forEach((l) => l());
  };

  return {
    request(id) {
      requests.add(id);
      update();
    },
    release(id) {
      requests.delete(id);
      update();
    },
    visible: () => current,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    }
  };
}

/** 페이지 전체가 함께 쓰는 저장소. */
export const floatingPromptStore = createFloatingPromptStore();

/**
 * 자리 요청과 반납. `wants` 가 참이면 자리를 요청하고 반납 함수를 돌려준다.
 * 훅의 effect 본문이다. DOM 없는 테스트에서도 확인할 수 있게 따로 뺐다.
 */
export function claimFloatingPromptSlot(
  store: FloatingPromptStore,
  id: FloatingPromptId,
  wants: boolean
): (() => void) | undefined {
  if (!wants) return undefined;
  store.request(id);
  return () => store.release(id);
}

/**
 * 카드 컴포넌트가 쓰는 훅. `wants` 가 참인 동안 자리를 요청하고, 지금 보여도 되는지
 * 돌려준다. `wants` 가 거짓이 되거나 컴포넌트가 사라지면 자리를 내놓는다.
 *
 * 서버 렌더링에서는 늘 거짓이다. 떠 있는 카드는 하이드레이션 뒤에만 뜬다.
 */
export function useFloatingPromptSlot(
  id: FloatingPromptId,
  wants: boolean,
  store: FloatingPromptStore = floatingPromptStore
): boolean {
  useEffect(() => claimFloatingPromptSlot(store, id, wants), [id, wants, store]);

  const visible = useSyncExternalStore(store.subscribe, store.visible, () => null);
  return wants && visible === id;
}
